import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, AlertCircle, Play, Pencil, Plus, XCircle } from "lucide-react";
import RecordEditDialog from "@/components/admin/RecordEditDialog";
import CancelSubscriptionDialog from "@/components/admin/CancelSubscriptionDialog";
import CreateSubscriptionSheet from "@/components/admin/CreateSubscriptionSheet";
import { subscriptionFields } from "@/components/admin/recordFields";
import { Button } from "@/components/ui/button";
import AdminLayout from "@/components/admin/AdminLayout";
import { deliveryWindowLabel } from "@/lib/delivery-dates";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type Sub = {
  id: string;
  user_id: string;
  status: string;
  tier_tubes: number;
  monthly_price_cents: number | null;
  currency: string | null;
  next_delivery_date: string | null;
  paused_until: string | null;
  paused_for_overdue: boolean | null;
  paused_for_overdue_invoice_id: string | null;
  pending_tier_tubes: number | null;
  pending_monthly_price_cents: number | null;
  pending_effective_date: string | null;
  cancelled_at: string | null;
  cancel_at_period_end: boolean | null;
  current_period_end: string | null;
  created_at: string;
};


type ProfileLite = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
};

const fmtDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("sv-SE", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

const fmtMoney = (cents: number | null, ccy: string | null) =>
  cents != null
    ? new Intl.NumberFormat("sv-SE", {
        style: "currency",
        currency: ccy || "SEK",
        maximumFractionDigits: 0,
      }).format(cents / 100)
    : "—";

const badgeFor = (s: string) => {
  switch (s) {
    case "active":
      return "bg-emerald-100 text-emerald-800 hover:bg-emerald-100";
    case "paused":
      return "bg-amber-100 text-amber-800 hover:bg-amber-100";
    case "cancelled":
      return "bg-rose-100 text-rose-800 hover:bg-rose-100";
    default:
      return "bg-slate-100 text-slate-800 hover:bg-slate-100";
  }
};

type FilterKey = "all" | "active" | "paused" | "cancelled" | "pending_change";

const AdminSubscriptionsPage = () => {
  const [subs, setSubs] = useState<Sub[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileLite>>({});
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterKey>("active");
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [editSub, setEditSub] = useState<Record<string, unknown> | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    const load = async () => {
      const { data: ss } = await supabase
        .from("subscriptions")
        .select("*")
        .order("created_at", { ascending: false });

      const list = (ss as Sub[]) || [];
      setSubs(list);

      const userIds = Array.from(new Set(list.map((s) => s.user_id)));
      if (userIds.length > 0) {
        const { data: ps } = await supabase
          .from("profiles")
          .select("id, email, first_name, last_name, company")
          .in("id", userIds);
        const map: Record<string, ProfileLite> = {};
        ((ps as ProfileLite[]) || []).forEach((p) => {
          map[p.id] = p;
        });
        setProfiles(map);
      }
      setLoading(false);
    };
    load();
  }, [reloadKey]);

  const counts = useMemo(
    () => ({
      all: subs.length,
      active: subs.filter((s) => s.status === "active").length,
      paused: subs.filter((s) => s.status === "paused").length,
      cancelled: subs.filter((s) => s.status === "cancelled").length,
      pending_change: subs.filter((s) => s.pending_tier_tubes != null).length,
    }),
    [subs],
  );

  const mrr = useMemo(
    () =>
      subs
        .filter((s) => s.status === "active")
        .reduce((acc, s) => acc + (s.monthly_price_cents ?? 0), 0),
    [subs],
  );

  const totalTubes = useMemo(
    () =>
      subs
        .filter((s) => s.status === "active")
        .reduce((acc, s) => acc + (s.tier_tubes ?? 0), 0),
    [subs],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return subs.filter((s) => {
      if (filter === "pending_change") {
        if (s.pending_tier_tubes == null) return false;
      } else if (filter !== "all" && s.status !== filter) {
        return false;
      }
      if (!q) return true;
      const p = profiles[s.user_id];
      if (!p) return false;
      const name = `${p.first_name ?? ""} ${p.last_name ?? ""}`.toLowerCase();
      return (
        name.includes(q) ||
        p.email.toLowerCase().includes(q) ||
        (p.company ?? "").toLowerCase().includes(q)
      );
    });
  }, [subs, profiles, search, filter]);

  const [resumingId, setResumingId] = useState<string | null>(null);
  const handleResume = async (id: string) => {
    if (resumingId) return;
    setResumingId(id);
    try {
      const { data, error } = await (supabase as any).functions.invoke(
        "admin-resume-subscription",
        { body: { subscription_id: id } },
      );
      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || "Kunde inte återuppta");
      }
      setSubs((prev) =>
        prev.map((s) =>
          s.id === id
            ? {
                ...s,
                status: "active",
                paused_for_overdue: false,
                paused_for_overdue_invoice_id: null,
                paused_until: null,
                next_delivery_date: data.next_delivery_date || s.next_delivery_date,
              }
            : s,
        ),
      );
    } catch (e: any) {
      alert(e.message || "Något gick fel");
    } finally {
      setResumingId(null);
    }
  };

  return (

    <AdminLayout title="Prenumerationer">
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <p className="text-sm text-muted-foreground max-w-xl">
            Prenumerationer som skapas här registreras direkt hos betalleverantören, med rätt paketpris,
            moms och – vid faktura – OCR-nummer och bankgiro.
          </p>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-2" />
            Nytt abonnemang
          </Button>
        </div>

        <CreateSubscriptionSheet
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={() => setReloadKey((k) => k + 1)}
        />

        {/* KPI summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Aktiva</div>
            <div className="text-2xl font-semibold text-foreground mt-1">{counts.active}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">MRR</div>
            <div className="text-2xl font-semibold text-foreground mt-1">
              {fmtMoney(mrr, "SEK")}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Tuber/månad
            </div>
            <div className="text-2xl font-semibold text-foreground mt-1">{totalTubes}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Pending ändringar
            </div>
            <div className="text-2xl font-semibold text-foreground mt-1">
              {counts.pending_change}
            </div>
          </Card>
        </div>

        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sök kund (namn, e-post, företag)…"
              className="pl-9"
            />
          </div>
          <Tabs value={filter} onValueChange={(v) => setFilter(v as FilterKey)}>
            <TabsList>
              <TabsTrigger value="active">Aktiva ({counts.active})</TabsTrigger>
              <TabsTrigger value="paused">Pausade ({counts.paused})</TabsTrigger>
              <TabsTrigger value="cancelled">Avslutade ({counts.cancelled})</TabsTrigger>
              <TabsTrigger value="pending_change">
                Pending ändring ({counts.pending_change})
              </TabsTrigger>
              <TabsTrigger value="all">Alla ({counts.all})</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kund</TableHead>
                <TableHead>Företag</TableHead>
                <TableHead>Paket</TableHead>
                <TableHead>Pris/mån</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Nästa leverans</TableHead>
                <TableHead>Planerad ändring</TableHead>
                <TableHead aria-label="Åtgärd" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-10">
                    Laddar…
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-10">
                    Inga prenumerationer matchar.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((s) => {
                  const p = profiles[s.user_id];
                  const name =
                    p && [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
                  return (
                    <TableRow key={s.id}>
                      <TableCell>
                        <Link
                          to={`/admin/customers/${s.user_id}`}
                          className="hover:underline font-medium"
                        >
                          {name || p?.email || "—"}
                        </Link>
                        {p?.email && name && (
                          <div className="text-xs text-muted-foreground">{p.email}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {p?.company || "—"}
                      </TableCell>
                      <TableCell className="text-sm">{s.tier_tubes} tuber</TableCell>
                      <TableCell className="text-sm">
                        {fmtMoney(s.monthly_price_cents, s.currency)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge
                            className={
                              s.paused_for_overdue
                                ? "bg-rose-100 text-rose-800 hover:bg-rose-100"
                                : s.cancel_at_period_end && s.status !== "cancelled"
                                  ? "bg-rose-100 text-rose-800 hover:bg-rose-100"
                                  : badgeFor(s.status)
                            }
                          >
                            {s.paused_for_overdue
                              ? "Pausad – förfallen"
                              : s.cancel_at_period_end && s.status !== "cancelled"
                                ? `Uppsagd – till ${fmtDate(s.current_period_end)}`
                                : s.status}
                          </Badge>
                          {s.paused_for_overdue && s.paused_for_overdue_invoice_id && (
                            <Link
                              to={`/admin/invoices?highlight=${s.paused_for_overdue_invoice_id}`}
                              className="text-[10px] text-rose-700 hover:underline"
                            >
                              Se faktura
                            </Link>
                          )}
                          {!s.paused_for_overdue && s.paused_until && (
                            <span className="text-[10px] text-muted-foreground">
                              till {fmtDate(s.paused_until)}
                            </span>
                          )}
                        </div>
                      </TableCell>

                      <TableCell className="text-sm text-muted-foreground">
                        <div className="flex flex-col">
                          <span>{fmtDate(s.next_delivery_date)}</span>
                          {s.next_delivery_date && (
                            <span className="text-[10px]">
                              Levereras {deliveryWindowLabel(s.next_delivery_date)}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {s.pending_tier_tubes != null ? (
                          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {s.pending_tier_tubes} tuber
                            {s.pending_effective_date &&
                              ` · ${fmtDate(s.pending_effective_date)}`}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {s.status === "paused" && s.paused_for_overdue && (
                            <button
                              onClick={() => handleResume(s.id)}
                              disabled={resumingId === s.id}
                              className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-800 disabled:opacity-50"
                            >
                              <Play className="h-3 w-3" />
                              {resumingId === s.id ? "Återupptar…" : "Återuppta"}
                            </button>
                          )}
                          {s.status !== "cancelled" && !s.cancel_at_period_end && (
                            <button
                              onClick={() =>
                                setCancelTarget({
                                  id: s.id,
                                  name: name || p?.email || "Kund",
                                })
                              }
                              className="inline-flex items-center gap-1 text-sm font-medium text-rose-700 hover:text-rose-800"
                            >
                              <XCircle className="h-3 w-3" />
                              Avsluta
                            </button>
                          )}
                          <button
                            onClick={() => setEditSub(s as unknown as Record<string, unknown>)}
                            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                            title="Redigera manuellt"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                          <Link
                            to={`/admin/customers/${s.user_id}`}
                            className="text-primary inline-flex items-center gap-1 text-sm hover:underline"
                          >
                            Öppna <ArrowRight className="h-3 w-3" />
                          </Link>
                        </div>
                      </TableCell>

                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      <CancelSubscriptionDialog
        open={!!cancelTarget}
        onOpenChange={(open) => !open && setCancelTarget(null)}
        subscriptionId={cancelTarget?.id ?? null}
        customerName={cancelTarget?.name}
        onCancelled={(res) => {
          const id = cancelTarget?.id;
          setSubs((prev) =>
            prev.map((s) =>
              s.id === id
                ? {
                    ...s,
                    status: res.cancel_at_period_end ? s.status : "cancelled",
                    cancel_at_period_end: res.cancel_at_period_end,
                    cancelled_at: new Date().toISOString(),
                  }
                : s,
            ),
          );
          setCancelTarget(null);
        }}
      />

      <RecordEditDialog
        open={!!editSub}
        onOpenChange={(open) => !open && setEditSub(null)}
        table="subscriptions"
        title="Redigera abonnemang"
        fields={subscriptionFields}
        record={editSub}
        onSaved={() => setReloadKey((k) => k + 1)}
      />
    </AdminLayout>
  );
};

export default AdminSubscriptionsPage;
